import React from "react";
import styled from "styled-components";

const ShortcutHelpOverlay = ({ open, onClose, onOpenGuide, sections }) => {
  if (!open) return null;

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Card onMouseDown={(event) => event.stopPropagation()}>
        <Header>
          <div>
            <Eyebrow>Keyboard Shortcuts</Eyebrow>
            <Title>Faster navigation for web and desktop</Title>
          </div>
          <CloseButton type="button" onClick={onClose} aria-label="Close shortcut help">
            Esc
          </CloseButton>
        </Header>

        <Body>
          {sections.map((section) => (
            <Section key={section.title}>
              <SectionTitle>{section.title}</SectionTitle>
              <ShortcutList>
                {section.items.map((shortcut) => (
                  <ShortcutRow key={shortcut.id}>
                    <ShortcutLabel>{shortcut.label}</ShortcutLabel>
                    <Combo>{shortcut.combo}</Combo>
                  </ShortcutRow>
                ))}
              </ShortcutList>
            </Section>
          ))}
        </Body>

        <Footer>
          <span>Browser defaults such as `Ctrl/Cmd+F` and `Ctrl/Cmd+N` remain untouched.</span>
          {onOpenGuide ? (
            <GuideButton
              type="button"
              onClick={() => {
                onClose?.();
                onOpenGuide();
              }}
            >
              Open full guide
            </GuideButton>
          ) : null}
        </Footer>
      </Card>
    </Overlay>
  );
};

export default ShortcutHelpOverlay;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 170;
  background: rgba(2, 6, 23, 0.7);
  backdrop-filter: blur(6px);
  padding: 18px;
  display: grid;
  place-items: center;
`;

const Card = styled.div`
  width: min(900px, 100%);
  max-height: min(88vh, 760px);
  overflow: auto;
  border-radius: 22px;
  background:
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.12), transparent 32%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.28);
`;

const Header = styled.div`
  padding: 20px 22px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
`;

const Eyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  font-weight: 800;
  color: var(--app-primary);
  margin-bottom: 6px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: clamp(1.15rem, 2vw, 1.6rem);
  color: #0f172a;
`;

const CloseButton = styled.button`
  border: 1px solid var(--app-primary-soft);
  background: var(--app-primary-soft);
  color: #0f172a;
  border-radius: 999px;
  padding: 8px 12px;
  font-weight: 800;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 18px 22px 10px;
  display: grid;
  gap: 18px;
`;

const Section = styled.section`
  display: grid;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
`;

const ShortcutList = styled.div`
  display: grid;
  gap: 10px;
`;

const ShortcutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(226, 232, 240, 0.95);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const ShortcutLabel = styled.span`
  color: #1e293b;
  font-weight: 700;
`;

const Combo = styled.code`
  border-radius: 999px;
  padding: 6px 10px;
  background: var(--app-primary-soft);
  color: var(--app-primary);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
`;

const Footer = styled.div`
  padding: 0 22px 20px;
  color: #64748b;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const GuideButton = styled.button`
  border: 1px solid var(--app-primary-soft);
  background: var(--app-primary-soft);
  color: #0f172a;
  border-radius: 999px;
  padding: 9px 12px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
`;
